/* eslint-disable max-len */
import { encodeHTMLEntities } from '@ds/swymkit/utils'
import dot from 'dot-object'

import type { IIdea } from '~/entities/content/idea/interfaces/idea'
import type { MEDIA_PROCESSING_STATUS_V2 } from '~/entities/content/media/composables/media-format'
import { MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1, MEDIA_PROCESSING_STATUS } from '~/entities/content/media/composables/media-format'
import type { IMedia } from '~/entities/content/media/interfaces/media'
import { IMediaType } from '~/entities/content/media/interfaces/media-type'
import type { WedoAttributes } from '~/entities/content/wedo/interfaces/wedo'
import type { IWiki } from '~/entities/content/wiki/interfaces/wiki'
import { _userParser } from '~/entities/user/user/api/parser-users'

import type { ContentTemplate, IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { IDerivedTypeData, IDerivedTypeMedia } from '~/features-shared/content-derived-type/interfaces/derived-types'
import { createStyles } from '~/features-shared/content-derived-type/utils/derived-types'
import type { Acl } from '~/features-shared/libs/interfaces/authorization'
import { WedoDS6WMaturity } from '~/features-shared/maturities/interfaces/exalead-maturities'
import { WedoMaturity } from '~/features-shared/maturities/interfaces/maturities'
import type { IMessageDetails, IMessageDetailsInternalEvent, ITimeline } from '~/features-shared/timeline/interfaces/timeline'
import { TimelineStatus } from '~/features-shared/timeline/interfaces/timeline'

import type { SwymResponse } from '~/interfaces/services-dto'

const COPY_CONTENT_PROPS = [
  ['_community.id', 'community_id'],
  // community subject uri for community content
  ['_community.subject6wuri', 'community_subject_uri'],
  // community subject uri for conversation content
  ['_community.uri', 'community_subject_uri'],
  ['container_uri', 'community_subject_uri'],
  ['_community.community_type', 'community_type'],
  ['_community.title', 'community_title'],
  ['preview_original_width', 'preview.width'],
  ['preview_original_height', 'preview.height'],
  ['preview_media_processing_version', 'preview.processing_version'],
  ['preview_media_processing_status', 'preview.status'],
  ['preview_media_type', 'preview.type'],
  ['media_type', 'preview.type'],
  ['preview_media_file_ext', 'preview.media_file_ext'],
  ['preview_fingerprint', 'preview.preview_fingerprint'],
  ['thumbnail_subject6wuri', 'thumbnail_id'],
  ['thumbnail_id', 'preview.subject_uri'],
  ['status.id', 'status_id'],
  ['status.color', 'status_color'],
  ['status.title', 'status_title'],
]
const MOVE_CONTENT_PROPS = [
  ['model_id', 'id'],
  ['subject6wuri', 'subject_uri'],
  ['publish_date', 'published_at'],
  ['pubdate', 'published_at'],
  ['creation_date', 'created_at'],
  ['crdate', 'created_at'],
  ['update', 'updated_at'],
  ['modification_date', 'updated_at'],
  ['moderation_date', 'moderated_at'],
  ['body', 'description'],
  ['question', 'description'],
  ['message', 'description'],
  ['see_more', 'seeMore'],
  // content from my contributions API
  ['container', '_community'],
  ['thumbnailsInfos', 'thumbnails_infos'],
  ['views', 'stats_viewed'], // API V2
]

const MOVE_MEDIA_PROPS = [
  ['preview_original_width', 'width'],
  ['preview_original_height', 'height'],
  ['preview_media_processing_version', 'processing_version'],
  ['preview_media_processing_status', 'status'],
  // content from my contributions API
]
const COPY_MEDIA_PROPS = [
  ['media_type', 'type'],
]

const MOVE_TIMELINE_PROPS = [
  ...MOVE_CONTENT_PROPS,
  ['message_id', 'id'],
  ['container_uri', 'community_subject_uri'],
]

function isMedia(content: IContent): content is IMedia {
  return content.model === IContentModel.Media
}

export function dateParser(date?: string) {
  if (date && !date.includes('T') && !date.endsWith('Z') && !date.startsWith('0000')) {
    // iOS needs to parse date with 'T'
    return new Date(`${date.split(' ')
      .join('T')}Z`)
      .toISOString()
  }
  if (date && date.startsWith('0000'))
    return ''

  return date ?? ''
}

export function getContentModelFromSubjectUri(subject_uri: string, options: { derived_type?: IDerivedTypeData } = {}) {
  const adapter = {
    communitypost: IContentModel.Post,
    media: IContentModel.Media,
    qnaquestion: IContentModel.Question,
    qnaanswer: IContentModel.Question,
    idea: IContentModel.Idea,
    wikitree: IContentModel.Wiki,
    wedo: IContentModel.Wedo,
    dsxplan: IContentModel.Wedo,
    uuid: IContentModel.Wedo,
    conference: IContentModel.Conference,
    survey: IContentModel.Survey,
  }

  const derivedAdapter = {
    communitypost: IContentModel.DerivedPost,
    idea: IContentModel.DerivedIdea,
  }

  const [serverType] = subject_uri.split(':')
    .splice(-2, 1)

  return options.derived_type?.id ? derivedAdapter[serverType] : adapter[serverType]
}

export function getContentModelForTemplate(content_type: string, options: { derived_type?: IDerivedTypeData } = {}) {
  const adapter = {
    post: IContentModel.Post,
    media: IContentModel.Media,
    question: IContentModel.Question,
    iquestion: IContentModel.Question,
    ideation: IContentModel.Idea,
    idea: IContentModel.Idea,
    wikipage: IContentModel.Wiki,
    wedo: IContentModel.Wedo,
    wiki: IContentModel.Wiki,
    survey: IContentModel.Survey,
    conference: IContentModel.Conference,
    rituals: IContentModel.Rituals,
    streaming: IContentModel.Streaming,
    derivedpost: IContentModel.DerivedPost,
    derivedidea: IContentModel.DerivedIdea,
  }

  const derivedAdapter = {
    post: IContentModel.DerivedPost,
    ideation: IContentModel.DerivedIdea,
  }

  return options.derived_type?.id ? derivedAdapter[content_type] : adapter[content_type]
}

function _wedoMaturityParser(content: ISwymContent & { attributes?: WedoAttributes }) {
  const maturity = content?.attributes?.['dsxplan:hasMaturity']?.['@id']
    ?.replace('dsxplan:', '') as 'Done' | 'InProgress' | 'Open'
  if (maturity === 'Done')
    return WedoMaturity.Done
  if (maturity === 'InProgress')
    return WedoMaturity.InProgress
  if (maturity === 'Open')
    return WedoMaturity.Open
}

function _wedoMaturityParserTimeline(content: ISwymContent) {
  const maturity = content['ds6w:status'] as WedoDS6WMaturity

  if (maturity === WedoDS6WMaturity.Done)
    return WedoMaturity.Done

  if (maturity === WedoDS6WMaturity.InProgress)
    return WedoMaturity.InProgress

  if (maturity === WedoDS6WMaturity.Open)
    return WedoMaturity.Open
}

export function v2ApiMediaTypeParser(type?: string): IMediaType {
  const MediaV2TypeMapping: Record<string, IMediaType> = {
    'PICTURE': IMediaType.Picture,
    'DOCUMENT': IMediaType.Document,
    '3D_MODEL': IMediaType.Model3D,
    'DRAWING': IMediaType.Drawing,
    'VIDEO': IMediaType.Video,
    'SOUND': IMediaType.Sound,
    'ANIMATED_PICTURE': IMediaType.AnimatedPicture,
    'SIMULATION_MEDIA': IMediaType.Simulation,
    'STORY': IMediaType.Story,
    'SKETCH': IMediaType.Sketch,
    'WHITEBOARD': IMediaType.Whiteboard,
    'HOMEBYME_MEDIA': IMediaType.HomeByMe,
  }
  if (type && Object.keys(MediaV2TypeMapping)
    .includes(type)) {
    return MediaV2TypeMapping[type]
  }
  return IMediaType.Picture
}

function _v2ApiThumbnailsParser(thumbnailInfos: Partial<IDerivedTypeMedia>[]) {
  return thumbnailInfos.map((thumbnailInfo) => {
    return {
      height: thumbnailInfo.height,
      width: thumbnailInfo.width,
      preview_original_height: thumbnailInfo.height,
      preview_original_width: thumbnailInfo.width,
      media_id: thumbnailInfo.subject6wuri,
      // V2 API changed and moved type to media_type. for the transition, support both
      media_type: v2ApiMediaTypeParser(thumbnailInfo.media_type),
      preview_media_type: v2ApiMediaTypeParser(thumbnailInfo.media_type),
      preview_fingerprint: thumbnailInfo.fingerprint,
      preview_media_file_ext: thumbnailInfo.extension,
      preview_media_processing_version: thumbnailInfo.processing_version,
      preview_media_processing_status: MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1[thumbnailInfo.processing_state]
        ?? MEDIA_PROCESSING_STATUS.OK,
    }
  })
}

function _v2ApiTypeACLParser(content: IContent) {
  const acl = { ...content.acl }
  content._acl = <Acl>{}

  Object.keys(acl)
    .forEach((key) => {
      content._acl[key] = { access: acl[key] }
    })
}

function _v2ApiParser(content: IContent) {
  content.media?.forEach((value) => {
    if (value.subject6wuri === content.thumbnail_id) {
      if (content.preview) {
        content.preview.width = value.width
        content.preview.height = value.height
        content.preview.preview_fingerprint = value.fingerprint
        content.preview.status = MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1[value.processing_state as MEDIA_PROCESSING_STATUS_V2]
      }
    }
  })

  if (content.derived_type) {
    content.derived_type.label = encodeHTMLEntities(content.derived_type.label)
    createStyles(content.derived_type)
  }

  if (content.media)
    content.thumbnails_infos = _v2ApiThumbnailsParser(content.media) as unknown as IMedia[]

  if (isMedia(content)) {
    const type = content.media_type
    content.media_type = v2ApiMediaTypeParser(type)
    content.type = v2ApiMediaTypeParser(type)
    content.status = MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1[content.processing_state as MEDIA_PROCESSING_STATUS_V2]
    ?? MEDIA_PROCESSING_STATUS.OK
  }

  if (!content._acl)
    _v2ApiTypeACLParser(content)

  if ([IContentModel.Idea, IContentModel.DerivedIdea].includes(content.model))
    (content as IIdea).out = (content as IContent & { out?: boolean }).out === true ? '1' : '0'
}

/**
 * TODO
 * /!\ Need to have unit test for the parsing /!\
 */
// eslint-disable-next-line max-statements, complexity
function _contentParser<T extends IContent>(content: ISwymContent, v2Api?: boolean): T {
  const contentParsed: T = { ...content } as unknown as T
  MOVE_CONTENT_PROPS.forEach(([from, to]) => {
    dot.move(
      from,
      to,
      contentParsed,
    )
  })
  COPY_CONTENT_PROPS.forEach(([from, to]) => {
    dot.copy(
      from,
      to,
      contentParsed,
      contentParsed,
    )
  })
  contentParsed.created_at = dateParser(contentParsed.created_at) as string
  contentParsed.updated_at = dateParser(contentParsed.updated_at)
  contentParsed.moderated_at = dateParser(contentParsed.moderated_at) as string
  contentParsed.published_at = dateParser(contentParsed.published_at) as string

  contentParsed.model = getContentModelFromSubjectUri(
    contentParsed.subject_uri,
    { derived_type: contentParsed.derived_type },
  )

  if (contentParsed.author)
    contentParsed.author = _userParser(content.author)

  if (contentParsed.published === undefined)
    contentParsed.published = true

  if (contentParsed.publication_state === 'DRAFT')
    contentParsed.published = false

  if (contentParsed.model === IContentModel.Wedo)
    contentParsed.maturity = _wedoMaturityParser(content)

  if (contentParsed.model === IContentModel.Media) {
    MOVE_MEDIA_PROPS.forEach(([from, to]) => {
      dot.move(
        from,
        to,
        contentParsed,
      )
    })
    COPY_MEDIA_PROPS.forEach(([from, to]) => {
      dot.copy(
        from,
        to,
        contentParsed,
        contentParsed,
      )
    })
  }

  if (v2Api)
    _v2ApiParser(contentParsed)

  if (contentParsed.model === IContentModel.Wiki) {
    if (typeof content.is_minor_edit === 'string')
      (contentParsed as unknown as IWiki).is_minor_edit = content.is_minor_edit === '1'
  }

  if (content._community?.community_type === 'externaldm')
    contentParsed.community_type = 'dm'

  // If not wedo, use content's subject uri and community id to build subject uri
  // else use community id as a tactical fix for wedo
  if (!contentParsed.community_subject_uri && contentParsed.community_id) {
    contentParsed.community_subject_uri
    = (contentParsed.model !== IContentModel.Wedo)
        ? [...contentParsed.subject_uri.split(':')
          .splice(0, 3), 'community', content.community_id]
          .join(':')
        : content.community_id as string
  }

  ['visibility', 'published'].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(contentParsed, key)
    // @ts-expect-error not typed
      && typeof contentParsed[key] === 'string'
    ) {
      // @ts-expect-error not typed
      contentParsed[key] = Number.parseInt(contentParsed[key], 10)
    }
  });

  ['published'].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(contentParsed, key))
    // @ts-expect-error not typed
      contentParsed[key] = !!contentParsed[key]
  })

  return contentParsed
}

// eslint-disable-next-line max-statements
function _contentTemplateParser(swymContent: ISwymContent): IContent {
  MOVE_CONTENT_PROPS.forEach(([from, to]) => {
    dot.move(
      from,
      to,
      swymContent,
    )
  })
  COPY_CONTENT_PROPS.forEach(([from, to]) => {
    dot.copy(
      from,
      to,
      swymContent,
      swymContent,
    )
  })

  const content = swymContent as unknown as ContentTemplate

  content.created_at = dateParser(content.created_at)
  content.updated_at = dateParser(content.updated_at)
  content.moderated_at = dateParser(content.moderated_at)
  content.published_at = dateParser(content.published_at)

  // swymContent.model = getContentModelFromSubjectUri(swymContent.subject_uri, { derived_type: swymContent.derived_type })
  content.model = getContentModelForTemplate(content.type.toLowerCase(), { derived_type: content.derived_type })

  if (content.author)
    content.author = _userParser(content.author)

  if (content.published === undefined)
    content.published = true

  if (content.model === IContentModel.Wedo)
    content.maturity = _wedoMaturityParser(swymContent)

  if (content.model === IContentModel.Media) {
    MOVE_MEDIA_PROPS.forEach(([from, to]) => {
      dot.move(
        from,
        to,
        content,
      )
    })
  }

  (['visibility', 'published'] as (keyof IContent)[]).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(content, key) && typeof content[key] === 'string')
      // @ts-expect-error not typable
      content[key] = Number.parseInt(content[key], 10)
  });

  (['published'] as (keyof IContent)[]).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(content, key))
    // @ts-expect-error not typable
      content[key] = !!content[key]
  })
  return content
}

export function contentParser<T extends IContent>(
  response: SwymResponse<ISwymContent>,
  v2Api?: boolean,
): SwymResponse<T> {
  return { ...response, result: _contentParser<T>(response.result, v2Api) }
}

export function contentTemplateParser(response: SwymResponse<ISwymContent>): SwymResponse<IContent> {
  return { ...response, result: _contentTemplateParser(response.result) }
}

export function contentTemplatesParser(response: SwymResponse<ISwymContent[]>): SwymResponse<IContent[]> {
  return { ...response, result: response.result.map(_contentTemplateParser) }
}

export function contentsParser<T extends IContent>(
  response: SwymResponse<ISwymContent[]>,
  v2Api?: boolean,
): SwymResponse<T[]> {
  return {
    ...response,
    result: response.result.map(content => _contentParser<T>(content, v2Api)),
  }
}

export function _timelineParser(content: ITimeline) {
  MOVE_TIMELINE_PROPS.forEach(([from, to]) => {
    dot.move(
      from,
      to,
      content,
    )
  })

  if (content.author)
    content.author = _userParser(content.author)

  if (content.message_type === 'INTERNAL_CONTENT' && (content.message_details as IContent)?.model) {
    const parsedContent = _contentParser(content.message_details as ISwymContent)

    if (parsedContent.model === IContentModel.Wedo && !parsedContent.maturity)
      parsedContent.maturity = _wedoMaturityParserTimeline(content.message_details as ISwymContent)

    content.message_details = parsedContent
  }

  if (content.message_type !== 'INTERNAL_CONTENT' && (content.message_details as IMessageDetails)?.parent_message) {
    (content.message_details as IMessageDetails)
      .parent_message.author = _userParser((content.message_details as IMessageDetails).parent_message?.author)
  }

  if (content.message_details) {
    const messageDetails = content.message_details as IMessageDetailsInternalEvent
    const model = messageDetails.subject_model?.toLowerCase()
      || getContentModelFromSubjectUri(content.subject_uri, { derived_type: messageDetails.derived_type })

    messageDetails.subject_model = getContentModelForTemplate(model, { derived_type: messageDetails.derived_type })
  }

  content.status = TimelineStatus.SENT
  return content
}

export function timelineParser(response: SwymResponse<ITimeline[]>): SwymResponse<ITimeline[]> {
  return {
    ...response,
    result: response.result.map(_timelineParser),
  }
}
